/**
 * Shared helper function to determine if back-to-top button should be shown
 *
 * @param {HTMLElement|Event} scrollContainerOrEvent - The scrolling container element or scroll event
 * @param {HTMLElement} headerElement - The header element (optional, for mobile detection)
 * @returns {boolean} - True if back-to-top button should be shown
 */
export function shouldShowBackToTop(scrollContainerOrEvent, headerElement = null) {
  const isMobile = window.innerWidth < 640; // sm breakpoint

  // Get scroll position - either from event target or from window on mobile
  let scrollTop;
  if (isMobile) {
    // On mobile, use window.scrollY
    scrollTop = window.scrollY || window.pageYOffset;
  } else {
    // On desktop, use the scroll container
    const el = scrollContainerOrEvent.target || scrollContainerOrEvent;
    scrollTop = el.scrollTop;
  }

  if (isMobile && headerElement) {
    const headerHeight = headerElement.offsetHeight;
    return scrollTop > headerHeight;
  } else if (isMobile) {
    return scrollTop > 100;
  } else {
    return scrollTop > 400;
  }
}

/**
 * Shared helper function to scroll to top
 *
 * @param {HTMLElement} scrollContainer - The scrollContainer ref element
 */
export function scrollToTopHelper(scrollContainer) {
  const isMobile = window.innerWidth < 640;
  if (isMobile) {
    // On mobile, scroll the window
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  } else {
    // On desktop, scroll the container
    if (scrollContainer) {
      scrollContainer.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }
}

/**
 * Fill viewport check for infinite scroll.
 * On tall screens, the initial batch of rows may fit entirely within the scroll
 * container without causing overflow. When that happens, @scroll never fires and
 * loadMore is never called. This function repeatedly loads more rows until the
 * container overflows or there is nothing left to load.
 *
 * Call this from init() (after data loads) — the container template also calls
 * it automatically via x-init on the scrollContainer.
 *
 * @param {Object} component - The Alpine component instance (must have hasMore, loading, loadMore, $refs.scrollContainer)
 */
export function fillViewport(component) {
  const isMobile = window.innerWidth < 640;
  if (isMobile) return; // Mobile uses window scroll, not container scroll

  const check = () => {
    const sc = component.$refs?.scrollContainer;
    if (!sc) return;

    // If the container content doesn't overflow and there are more rows, load them
    if (sc.scrollHeight <= sc.clientHeight && component.hasMore && !component.loading) {
      component.loadMore();
      // Re-check after loadMore completes (it uses a 300ms timeout internally)
      setTimeout(check, 350);
    }
  };

  // Delay initial check to let Alpine render the current batch
  setTimeout(check, 100);
}

/**
 * Initialize mobile scroll handlers for stacked list
 * Call this from your init() method on mobile
 *
 * @param {Object} component - The Alpine component instance (this)
 * @param {Object} options - Configuration options
 * @param {number} options.scrollThreshold - Distance from bottom to trigger load (default: 200)
 */
export function initMobileScrollHandlers(component, options = {}) {
  const scrollThreshold = options.scrollThreshold || 200;

  component.windowScrollHandler = () => {
    // Update back-to-top button visibility
    const scrollTop = window.scrollY || window.pageYOffset;
    const headerElement = component.$refs.header;

    if (headerElement) {
      const headerHeight = headerElement.offsetHeight;
      component.showBackToTop = scrollTop > headerHeight;
    } else {
      component.showBackToTop = scrollTop > 100;
    }

    // Handle infinite scroll on mobile
    const scrollPosition = window.scrollY + window.innerHeight;
    const threshold = document.documentElement.scrollHeight - scrollThreshold;

    if (scrollPosition >= threshold && component.hasMore && !component.loading) {
      component.loadMore();
    }
  };

  window.addEventListener('scroll', component.windowScrollHandler);
}

/**
 * Stacked List Manager - Reusable Alpine.js Component
 *
 * Provides search, filter, sort, infinite scroll, and Action Cable real-time updates
 * for stacked list views (certificates, inspections, keys, landlords, etc.)
 *
 * @param {Object} config - Configuration object
 * @param {string} config.dataAttribute - Name of the data attribute containing initial data (e.g., 'certificates')
 * @param {Function} config.transformRow - Function to transform API data into row format
 * @param {Object} config.sort - Default sort configuration { column: string, asc: boolean }
 * @param {string} config.defaultFilter - Default filter value (e.g., 'Active', 'all')
 * @param {Function} config.filterFunction - Custom filter logic (optional)
 * @param {Object} config.sortLabels - Labels for sort columns
 * @param {Function} config.statusCounts - Custom status count logic (optional)
 * @param {string} config.searchEndpoint - URL for search requests
 * @param {string} config.actionCableType - Message type to listen for (e.g., 'certificate', 'inspection')
 * @param {Function} config.actionCableFilter - Filter for Action Cable messages (optional)
 * @param {number} config.batchSize - Number of items per infinite scroll batch (default: 20)
 * @param {number} config.scrollThreshold - Pixels from bottom to trigger load (default: 200)
 *
 * @returns {Object} Alpine.js data object
 */
export function StackedListManager(config = {}) {
  return {
    // State
    rows: [],
    allRows: [],
    originalData: [],
    sort: config.sort || { column: "createdAt", asc: false },
    searchQuery: '',
    searching: false,
    statusFilter: config.defaultFilter || 'all',

    // Infinite scroll state
    visibleCount: config.batchSize || 20,
    batchSize: config.batchSize || 20,
    loading: false,
    showBackToTop: false,

    // Action Cable subscription
    subscription: null,

    /**
     * Initialize the component
     */
    init() {
      const self = this;

      // Load data from data attribute
      const dataAttr = config.dataAttribute || 'items';
      this.originalData = JSON.parse(this.$el.dataset[dataAttr] || '[]');

      // Transform rows using provided function
      if (config.transformRow) {
        this.rows = this.originalData.map(config.transformRow);
      } else {
        this.rows = this.originalData;
      }
      this.allRows = this.rows;

      // On mobile, listen to window scroll for back-to-top AND infinite scroll
      const isMobile = window.innerWidth < 640;
      if (isMobile) {
        const self = this;
        this.windowScrollHandler = () => {
          // Update back-to-top button visibility
          const scrollTop = window.scrollY || window.pageYOffset;
          const headerElement = self.$refs.header;

          if (headerElement) {
            const headerHeight = headerElement.offsetHeight;
            self.showBackToTop = scrollTop > headerHeight;
          } else {
            self.showBackToTop = scrollTop > 100;
          }

          // Handle infinite scroll on mobile
          const scrollPosition = window.scrollY + window.innerHeight;
          const threshold = document.documentElement.scrollHeight - (config.scrollThreshold || 200);

          if (scrollPosition >= threshold && self.hasMore && !self.loading) {
            self.loadMore();
          }
        };
        window.addEventListener('scroll', this.windowScrollHandler);
      }

      // Subscribe to Action Cable if configured
      if (config.actionCableType) {
        this.subscription = consumer.subscriptions.create(
          { channel: "DataUpdatesChannel" },
          {
            connected() {
              // Connected to real-time updates
            },

            disconnected() {
              // Disconnected from real-time updates
            },

            received(message) {
              if (message.type === config.actionCableType) {
                // Apply optional filter
                if (config.actionCableFilter && !config.actionCableFilter(message)) {
                  return;
                }
                self.handleUpdate(message);
              }
            }
          }
        );
      }

      // Register cleanup callback for when element is removed from DOM (Turbo navigation)
      this.$cleanup(() => this.destroy());
    },

    /**
     * Computed: Filtered rows based on current filter
     */
    get filteredRows() {
      if (this.statusFilter === 'all') {
        return this.rows;
      }

      // Use custom filter function if provided
      if (config.filterFunction) {
        return this.rows.filter(row => config.filterFunction(row, this.statusFilter));
      }

      // Default: filter by status field
      return this.rows.filter(row => row.status === this.statusFilter);
    },

    /**
     * Computed: Label for current sort column
     */
    get sortLabel() {
      const labels = config.sortLabels || {
        'createdAt': 'Created',
        'updatedAt': 'Updated'
      };
      return labels[this.sort.column] || 'Sort';
    },

    /**
     * Computed: Count of items by status (customizable)
     */
    get statusCounts() {
      if (config.statusCounts) {
        return config.statusCounts(this.rows);
      }

      // Default: just return total
      return {
        total: this.rows.length
      };
    },

    /**
     * Computed: Sorted rows
     */
    get sortedRows() {
      let sorted = [...this.filteredRows];
      if (this.sort.column) {
        sorted.sort((a, b) => {
          let valA = a[this.sort.column];
          let valB = b[this.sort.column];

          // Date sorting
          if (valA instanceof Date || (typeof valA === 'string' && valA.match(/^\d{4}-\d{2}-\d{2}/))) {
            return (new Date(valA) - new Date(valB)) * (this.sort.asc ? 1 : -1);
          }

          // Null/undefined handling
          if (!valA) valA = '';
          if (!valB) valB = '';

          // String/number comparison
          if (valA < valB) return this.sort.asc ? -1 : 1;
          if (valA > valB) return this.sort.asc ? 1 : -1;
          return 0;
        });
      }
      return sorted;
    },

    /**
     * Computed: Visible rows (for infinite scroll)
     */
    get visibleRows() {
      return this.sortedRows.slice(0, this.visibleCount);
    },

    /**
     * Computed: Whether there are more items to load
     */
    get hasMore() {
      return this.visibleCount < this.sortedRows.length;
    },

    /**
     * Handle scroll event for infinite scroll
     */
    handleScroll(event) {
      const isMobile = window.innerWidth < 640;

      // Show/hide back to top button using shared helper
      this.showBackToTop = shouldShowBackToTop(event, this.$refs.header);

      // Handle infinite scroll - only on desktop for now
      if (!isMobile && event && event.target) {
        const el = event.target;
        const scrollPosition = el.scrollTop + el.clientHeight;
        const threshold = el.scrollHeight - (config.scrollThreshold || 200);

        // Load more if near bottom
        if (scrollPosition >= threshold && this.hasMore && !this.loading) {
          this.loadMore();
        }
      }
    },

    /**
     * Scroll to top of list
     */
    scrollToTop() {
      scrollToTopHelper(this.$refs.scrollContainer);
    },

    /**
     * Load more items (infinite scroll)
     */
    loadMore() {
      if (this.loading || !this.hasMore) return;

      this.loading = true;

      // Simulate slight delay for smooth UX
      setTimeout(() => {
        this.visibleCount += this.batchSize;
        this.loading = false;
      }, 300);
    },

    /**
     * Reset scroll position to first batch
     */
    resetScroll() {
      this.visibleCount = this.batchSize;
    },

    /**
     * Apply filter
     */
    filterByStatus(status) {
      this.statusFilter = status;
      this.resetScroll();
    },

    /**
     * Sort by column
     */
    sortBy(col) {
      if (this.sort.column === col) {
        this.sort.asc = !this.sort.asc;
      } else {
        this.sort.column = col;
        this.sort.asc = true;
      }
      this.resetScroll();
    },

    /**
     * Toggle sort direction
     */
    toggleSortDirection() {
      this.sort.asc = !this.sort.asc;
      this.resetScroll();
    },

    /**
     * Perform search
     */
    search() {
      const query = this.searchQuery.trim();

      // Clear search
      if (query === '') {
        this.rows = this.allRows;
        this.resetScroll();
        this.searching = false;
        return;
      }

      // Prevent duplicate requests
      if (this.searching) return;
      this.searching = true;

      // Use configured search endpoint
      const searchUrl = config.searchEndpoint
        ? `${config.searchEndpoint}?q=${encodeURIComponent(query)}`
        : null;

      if (!searchUrl) {
        console.error('Search endpoint not configured');
        this.searching = false;
        return;
      }

      fetch(searchUrl, {
        headers: { 'Accept': 'application/json' }
      })
      .then(response => response.json())
      .then(data => {
        if (config.transformRow) {
          this.rows = data.map(config.transformRow);
        } else {
          this.rows = data;
        }
        this.resetScroll();
      })
      .catch(error => {
        console.error('Search error:', error);
      })
      .finally(() => {
        this.searching = false;
      });
    },

    /**
     * Handle Action Cable update
     */
    handleUpdate(message) {
      if (!config.transformRow) {
        console.warn('transformRow not configured for Action Cable updates');
        return;
      }

      switch(message.action) {
        case 'update':
          const index = this.rows.findIndex(r => r.id === message.id);
          if (index !== -1) {
            this.rows[index] = config.transformRow(message.data);
            this.allRows = this.rows;
          }
          break;

        case 'create':
          const newRow = config.transformRow(message.data);
          this.rows.push(newRow);
          this.allRows = this.rows;
          break;

        case 'destroy':
          this.rows = this.rows.filter(r => r.id !== message.id);
          this.allRows = this.rows;
          break;
      }
    },

    /**
     * Cleanup on component destroy
     */
    destroy() {
      if (this.subscription) {
        this.subscription.unsubscribe();
      }
      // Clean up window scroll listener if it exists
      if (this.windowScrollHandler) {
        window.removeEventListener('scroll', this.windowScrollHandler);
      }
    }
  };
}
